import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { StatusBadge } from "../components/etams/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatDateTime } from "../lib/format";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../components/etams/PalangDaftar";
import { apiErrorMessage } from "../lib/inventoryApi";
import Hint from "../components/etams/Hint";

const KOLOM_CSV = [
  { kunci: "code", judul: "Kode" },
  { kunci: "name", judul: "Nama" },
  { kunci: "region_ids", judul: "Wilayah", ambil: (g) => (g.region_ids || []).join(" | ") },
  { kunci: "active", judul: "Aktif", ambil: (g) => (g.active ? "Ya" : "Tidak") },
  { kunci: "updated_at", judul: "Diperbarui", ambil: (g) => formatDateTime(g.updated_at) },
];

export default function MasterPriceGroups() {
  const { isOwner, isKepalaCabang, profile } = useAuth();
  // K177 bagian 1 (20 Sep 2026) MEMBALIK kunci K169 (13 Sep, `bolehTulis = false`):
  // master harga ditulis dari layar oleh Owner (semua region) dan Kepala Cabang
  // (region SENDIRI) — SESUDAH empat pagar K169 §4 berdiri di router
  // (`services/pagar_harga.py`, 21 Sep 2026). Layar hanya menyembunyikan tombol;
  // yang menjaga tetap router (REGION_FORBIDDEN / GROUP_AKTIF_GANDA_REGION / …).
  const bolehTulis = isOwner || isKepalaCabang;
  const regionSaya = profile?.region_id || null;
  const bolehTulisGroup = (g) => isOwner || (isKepalaCabang && regionSaya
    && (g?.region_ids || []).length === 1 && g.region_ids[0] === regionSaya);
  const [items, setItems] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pg, rg] = await Promise.all([
        api.get("/master/price-groups"),
        api.get("/regions"),
      ]);
      setItems(pg.data.items || []);
      setRegions(rg.data || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat data"));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const daftar = useDaftar(items, {
    kunciCari: ["code", "name"],
    urutAwal: { kunci: "code", arah: "asc" },
  });

  const regionName = (id) => regions.find((r) => r.id === id)?.name || "—";
  // Ekspor memakai NAMA wilayah, bukan ID teknis (K200 butir 6).
  const kolomCsv = KOLOM_CSV.map((k) => (k.kunci === "region_ids"
    ? { ...k, judul: "Wilayah", ambil: (g) => (g.region_ids || []).map(regionName).join(" | ") }
    : k));

  const handleDelete = async () => {
    try {
      const r = await api.delete(`/master/price-groups/${confirmDel.id}`);
      toast.success(r.data.soft ? "Kelompok harga dinonaktifkan (masih dipakai daftar harga)" : "Kelompok harga dihapus");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal menghapus"));
    } finally { setConfirmDel(null); }
  };

  return (
    <div className="space-y-6" data-testid="master-price-groups-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            Kelompok Harga{" "}
            <Hint>Kelompok harga per wilayah. Setiap baris daftar harga menginduk ke salah satu kelompok ini.</Hint>
          </h1>
        <p className="text-sm rounded-md border border-border bg-muted px-3 py-2 mt-2" data-testid="pg-aturan">
          <strong>Satu wilayah, satu kelompok aktif.</strong> Owner mengatur semua wilayah; Kepala Cabang hanya kelompok wilayahnya sendiri. Kelompok yang masih dipakai daftar harga tidak dihapus, melainkan dinonaktifkan.
        </p>
        </div>
        {bolehTulis && (
          <Button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="pg-new-btn">
            <Plus size={16} className="mr-1" /> Tambah Kelompok Harga
          </Button>
        )}
      </div>

      <Card className="p-4">
        <PalangDaftar daftar={daftar} placeholder="Cari kode / nama…" testId="pg-palang"
                      ekspor={{ nama: "master_kelompok_harga", kolom: kolomCsv }} />
        {loading ? <div className="py-8 text-center text-sm text-muted-foreground">Memuat...</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
                <tr>
                  <ThUrut daftar={daftar} kunci="code" className="text-left px-3 py-2">Kode</ThUrut>
                  <ThUrut daftar={daftar} kunci="name" className="text-left px-3 py-2">Nama</ThUrut>
                  <th className="text-left px-3 py-2">Wilayah</th>
                  <ThUrut daftar={daftar} kunci="active" className="text-left px-3 py-2">Status</ThUrut>
                  <ThUrut daftar={daftar} kunci="updated_at" className="text-left px-3 py-2">Diperbarui</ThUrut>
                  {bolehTulis && <th className="px-3 py-2"></th>}
                </tr>
              </thead>
              <tbody>
                {daftar.total === 0 && <tr><td colSpan={bolehTulis ? 6 : 5} className="px-3 py-8 text-center text-muted-foreground">
                  {daftar.cari.trim() ? "Tidak ada yang cocok." : "Belum ada kelompok harga."}
                </td></tr>}
                {daftar.tampil.map((g) => (
                  <tr key={g.id} className="border-t border-border" data-testid={`pg-row-${g.id}`}>
                    <td className="px-3 py-2 font-medium font-mono text-xs">{g.code}</td>
                    <td className="px-3 py-2">{g.name}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {(g.region_ids || []).map((rid) => (
                          <Badge key={rid} variant="outline" className="text-xs">{regionName(rid)}</Badge>
                        ))}
                        {(g.region_ids || []).length === 0 && <span className="text-muted-foreground text-xs">—</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2">{g.active ? <StatusBadge tone="success">Aktif</StatusBadge> : <Badge variant="outline">Nonaktif</Badge>}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{formatDateTime(g.updated_at)}</td>
                    {bolehTulis && (
                      <td className="px-3 py-2 text-right">
                        {bolehTulisGroup(g) ? (<>
                          <Button variant="ghost" size="icon" onClick={() => { setEditing(g); setShowForm(true); }} data-testid={`pg-edit-${g.id}`}><Edit size={14} /></Button>
                          <Button variant="ghost" size="icon" onClick={() => setConfirmDel(g)} data-testid={`pg-delete-${g.id}`} className="text-danger-soft-foreground"><Trash2 size={14} /></Button>
                        </>) : <span className="text-xs text-muted-foreground">wilayah lain</span>}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            <Paginasi daftar={daftar} testId="pg-paginasi" />
          </div>
        )}
      </Card>

      {showForm && (
        <PgFormModal pg={editing} regions={isOwner ? regions : regions.filter((r) => r.id === regionSaya)} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={() => { setShowForm(false); setEditing(null); load(); }} />
      )}

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus kelompok harga?</AlertDialogTitle>
            <AlertDialogDescription>
              "{confirmDel?.name}" akan dihapus. Jika masih dipakai daftar harga, kelompok ini hanya dinonaktifkan supaya data harga tetap utuh.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="pg-delete-confirm">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function PgFormModal({ pg, regions, onClose, onSaved }) {
  const [form, setForm] = useState({
    code: pg?.code || "",
    name: pg?.name || "",
    region_ids: pg?.region_ids || [],
    active: pg?.active ?? true,
    notes: pg?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleRegion = (id) => upd("region_ids", form.region_ids.includes(id) ? form.region_ids.filter((x) => x !== id) : [...form.region_ids, id]);

  const save = async () => {
    if (!form.code.trim() || !form.name.trim()) { toast.error("Kode dan Nama wajib diisi"); return; }
    setSaving(true);
    try {
      const body = { code: form.code.trim(), name: form.name.trim(), region_ids: form.region_ids, active: form.active, notes: form.notes || null };
      if (pg) await api.patch(`/master/price-groups/${pg.id}`, body);
      else await api.post("/master/price-groups", body);
      toast.success("Kelompok harga disimpan");
      onSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal menyimpan"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="pg-form-modal">
        <DialogHeader><DialogTitle>{pg ? "Ubah Kelompok Harga" : "Tambah Kelompok Harga"}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>Kode *</Label><Input value={form.code} onChange={(e) => upd("code", e.target.value)} placeholder="cth: SBY_2026" data-testid="pg-form-code" /></div>
          <div><Label>Nama *</Label><Input value={form.name} onChange={(e) => upd("name", e.target.value)} placeholder="cth: Surabaya 2026" data-testid="pg-form-name" /></div>
          <div>
            <Label>Wilayah (boleh lebih dari satu)</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {regions.map((r) => (
                <label key={r.id} className="flex items-center gap-1.5 cursor-pointer px-2 py-1 border rounded-md text-sm">
                  <Checkbox checked={form.region_ids.includes(r.id)} onCheckedChange={() => toggleRegion(r.id)} data-testid={`pg-form-region-${r.id}`} />
                  {r.name}
                </label>
              ))}
            </div>
            {/* BUSINESS-06 (K190 butir 10): aturan tanggal berlaku group dua zona
                DITULIS DI LAYAR supaya yang dilihat Owner = yang tersimpan. */}
            <p className="mt-1.5 text-xs text-muted-foreground" data-testid="pg-form-aturan-tanggal">
              Tanggal berlaku harga yang dikosongkan mengikuti <b>tanggal paling awal</b> di antara zona waktu
              wilayah kelompok ini (kelompok WIB + WITA pada 23.00–23.59 WIB memakai tanggal WIB).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox checked={form.active} onCheckedChange={(v) => upd("active", !!v)} id="pgactive" data-testid="pg-form-active" />
            <Label htmlFor="pgactive" className="cursor-pointer">Aktif</Label>
          </div>
          <div><Label>Catatan</Label><Textarea rows={2} value={form.notes} onChange={(e) => upd("notes", e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={saving} data-testid="pg-form-save">{saving ? "Menyimpan..." : "Simpan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
